import React, { Suspense, lazy, useCallback, useState } from 'react';

import { toast } from '@/design-system/toaster';
import type { PackageRow } from '@/services/api/packages';
import {
  shipmentsAPI,
  type Shipment,
  type ShipmentClientSnapshot,
} from '@/services/api/shipments';

import {
  MAX_BULK_LABEL_SHIPMENTS,
  shipmentToPackageRows,
} from './shipmentLabels';

// Lazy so the react-pdf bundle only loads when a user actually prints.
const LabelPreviewModal = lazy(() => import('./index'));

interface UseShipmentLabelsOptions {
  /**
   * Backs the label header when a shipment has no `client` snapshot (rows
   * saved before the BE stored one) — e.g. a provider-name lookup.
   */
  fallbackClient?: (shipment: Shipment) => ShipmentClientSnapshot | null;
}

/**
 * Print shipping labels for whole shipments with the shared label modal.
 * One place for the fetch → map → modal flow, used by the Shipments list and
 * detail panel and the WMS outbound request detail.
 *
 *   const labels = useShipmentLabels();
 *   <Button onClick={() => labels.print([shipmentId])} />   // fetches items
 *   <Button onClick={() => labels.open(rows)} />            // rows in hand
 *   {labels.modal}
 */
export const useShipmentLabels = ({
  fallbackClient,
}: UseShipmentLabelsOptions = {}) => {
  // Label rows shown in the modal; null = closed.
  const [packages, setPackages] = useState<PackageRow[] | null>(null);
  const [preparing, setPreparing] = useState(false);

  /** Open the modal with label rows already built (no request). */
  const open = useCallback((rows: PackageRow[]) => {
    if (rows.length === 0) {
      toast.error('Nothing to print', {
        description: 'This shipment has no items to label.',
      });
      return;
    }
    setPackages(rows);
  }, []);

  /**
   * Fetch the shipments' items in one `items:bulk` round-trip (list rows and
   * other features don't carry them), map them and open the modal.
   */
  const print = useCallback(
    async (shipmentIds: string[]) => {
      if (shipmentIds.length === 0) {
        toast.error('No shipments selected', {
          description: 'Select at least one shipment to print.',
        });
        return;
      }
      if (shipmentIds.length > MAX_BULK_LABEL_SHIPMENTS) {
        toast.error('Too many shipments', {
          description: `Maximum ${MAX_BULK_LABEL_SHIPMENTS} shipments per print. Select ${MAX_BULK_LABEL_SHIPMENTS} or fewer.`,
        });
        return;
      }
      setPreparing(true);
      // A menu item closes on select — this is its only progress feedback.
      const loadingId = toast.loading('Preparing labels…');
      try {
        const shipments = await shipmentsAPI.bulkItems(shipmentIds);
        const rows = shipments.flatMap((s) =>
          shipmentToPackageRows(s, fallbackClient?.(s))
        );
        if (rows.length === 0) {
          toast.error('Nothing to print', {
            description:
              shipmentIds.length > 1
                ? 'The selected shipments have no items to label.'
                : 'This shipment has no items to label.',
          });
          return;
        }
        setPackages(rows);
      } catch (err) {
        toast.error('Could not load items', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      } finally {
        toast.dismiss(loadingId);
        setPreparing(false);
      }
    },
    [fallbackClient]
  );

  const modal = packages ? (
    <Suspense fallback={null}>
      <LabelPreviewModal
        open
        onClose={() => setPackages(null)}
        packages={packages}
      />
    </Suspense>
  ) : null;

  return { print, open, preparing, modal };
};

export default useShipmentLabels;
